/**
 * ROSEVIBE — motion system.
 * Scroll reveals, header state, and drawer/panel chrome. All effects
 * degrade to instant state changes under prefers-reduced-motion, handled
 * primarily in CSS; this file only avoids adding extra observers when it
 * would be pointless work.
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --------------------------------------------------------------------
   * Scroll reveal system
   *
   * The CSS hides [data-rv-reveal] elements ONLY under a .rv-anim-ready
   * root class, which we add here. So if this script ever fails to run,
   * nothing is left stuck invisible — content just shows with no motion.
   * A per-element timeout is the second safety net: if the observer never
   * fires for something (odd layouts, print, etc.) it reveals anyway.
   * ------------------------------------------------------------------ */
  function initReveals() {
    var selector = '[data-rv-reveal], [data-rv-reveal-scale], [data-rv-hairline], .rv-mask-line';
    var targets = Array.prototype.slice.call(document.querySelectorAll(selector));
    if (!targets.length) return;

    var root = document.documentElement;
    root.classList.add('rv-anim-ready');
    root.classList.add('rv-anim-active'); // signal the head fallback to stand down

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      // fire a touch before the element fully enters, so the motion reads
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );

    targets.forEach(function (el) { observer.observe(el); });

    // safety net: anything still hidden after 2.5s reveals regardless
    setTimeout(function () {
      targets.forEach(function (el) {
        if (!el.classList.contains('is-revealed')) el.classList.add('is-revealed');
      });
    }, 2500);
  }

  /* --------------------------------------------------------------------
   * Scroll parallax — elements marked .rv-parallax get a gentle counter-
   * scroll translate on their inner content, driven off scroll position.
   * ------------------------------------------------------------------ */
  function initParallax() {
    if (prefersReducedMotion) return;
    var items = Array.prototype.slice.call(document.querySelectorAll('.rv-parallax'));
    if (!items.length) return;

    var ticking = false;
    function update() {
      var vh = window.innerHeight;
      items.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        var strength = parseFloat(el.getAttribute('data-rv-parallax')) || 0.12;
        // progress: -1 (below) .. 1 (above) relative to viewport center
        var progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        var shift = -(progress * strength * 100);
        el.style.setProperty('--rv-parallax', shift.toFixed(1) + 'px');
      });
      ticking = false;
    }
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* --------------------------------------------------------------------
   * Header scroll state (compacts on scroll, glass background)
   * ------------------------------------------------------------------ */
  /* header stays sticky and always visible while scrolling — it only ever
     toggles the compact "is-scrolled" style, it never hides itself */
  function initHeaderScroll() {
    var header = document.querySelector('[data-rv-header]');
    if (!header) return;

    var lastScrolledToggle = false;

    function update() {
      var scrolled = window.scrollY > 24;
      if (scrolled !== lastScrolledToggle) {
        header.classList.toggle('is-scrolled', scrolled);
        lastScrolledToggle = scrolled;
      }
    }

    update();
    window.addEventListener('scroll', throttle(update, 100), { passive: true });
  }

  /* --------------------------------------------------------------------
   * Announcement bar rotation
   * ------------------------------------------------------------------ */
  function initAnnouncementBar() {
    var bar = document.querySelector('[data-rv-announce]');
    if (!bar) return;
    var items = bar.querySelectorAll('.rv-announce__item');
    if (items.length < 2) return;

    var interval = parseInt(bar.getAttribute('data-rv-announce-interval'), 10) || 5000;
    var index = 0;

    setInterval(function () {
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    }, interval);
  }

  /* --------------------------------------------------------------------
   * Header search panel toggle
   * ------------------------------------------------------------------ */
  function initSearchToggle() {
    var toggles = document.querySelectorAll('[data-rv-toggle-search]');
    var panel = document.querySelector('[data-rv-search-panel]');
    if (!panel || !toggles.length) return;

    toggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var open = panel.getAttribute('aria-hidden') === 'false';
        panel.setAttribute('aria-hidden', open ? 'true' : 'false');
        if (!open) {
          var input = panel.querySelector('input[type="search"]');
          if (input) setTimeout(function () { input.focus(); }, 350);
        }
      });
    });
  }

  /* --------------------------------------------------------------------
   * Generic drawer system (cart + mobile menu)
   * ------------------------------------------------------------------ */
  var lastFocusedEl = null;

  function openDrawer(drawer) {
    if (!drawer) return;
    lastFocusedEl = document.activeElement;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('rv-lock');
    var closeBtn = drawer.querySelector('[data-rv-drawer-close]');
    if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 400);
  }

  function closeDrawer(drawer) {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('rv-lock');
    if (lastFocusedEl && typeof lastFocusedEl.focus === 'function') {
      lastFocusedEl.focus();
    }
  }

  function initDrawers() {
    var cartDrawer = document.getElementById('rv-cart-drawer');
    var mobileMenu = document.getElementById('rv-mobile-menu');
    var menuBtns = document.querySelectorAll('[data-rv-open-mobile-menu]');

    function setMenuBtnOpen(open) {
      menuBtns.forEach(function (btn) {
        btn.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', String(open));
      });
    }

    document.querySelectorAll('[data-rv-open-cart]').forEach(function (btn) {
      btn.addEventListener('click', function () { openDrawer(cartDrawer); });
    });
    menuBtns.forEach(function (btn) {
      btn.addEventListener('click', function () { openDrawer(mobileMenu); setMenuBtnOpen(true); });
    });
    document.querySelectorAll('[data-rv-drawer-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        var drawer = el.closest('.rv-drawer');
        closeDrawer(drawer);
        if (drawer === mobileMenu) setMenuBtnOpen(false);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('.rv-drawer[aria-hidden="false"]').forEach(function (drawer) {
        closeDrawer(drawer);
        if (drawer === mobileMenu) setMenuBtnOpen(false);
      });
    });

    // exposed for theme.js to call after AJAX cart updates
    window.RV = window.RV || {};
    window.RV.openCartDrawer = function () { openDrawer(cartDrawer); };
    window.RV.closeCartDrawer = function () { closeDrawer(cartDrawer); };
  }

  /* --------------------------------------------------------------------
   * Hero slider — full-bleed cross-fading slides with a real timed-fill
   * dot progress (same technique as the testimonials rail: reset then
   * transition width to 100% over the auto-advance duration so the dot
   * itself IS the countdown, not just a static indicator).
   * ------------------------------------------------------------------ */
  function initHeroSlider() {
    var sliders = document.querySelectorAll('[data-rv-hero-slider]');
    if (!sliders.length) return;

    sliders.forEach(function (root) {
      var slides = Array.prototype.slice.call(root.querySelectorAll('[data-rv-hero-slide]'));
      var dots = Array.prototype.slice.call(root.querySelectorAll('[data-rv-hero-dot]'));
      var prevBtn = root.querySelector('[data-rv-hero-prev]');
      var nextBtn = root.querySelector('[data-rv-hero-next]');
      if (slides.length < 2) return;

      var index = 0;
      var autoAdvanceMs = 7000;
      var timer = null;

      function setDotFill(dot, active) {
        var fill = dot.querySelector('[data-rv-hero-dot-fill]');
        if (!fill) return;
        fill.style.transition = 'none';
        fill.style.width = '0%';
        if (!active) return;
        void fill.offsetWidth;
        if (prefersReducedMotion) { fill.style.width = '100%'; return; }
        fill.style.transition = 'width ' + autoAdvanceMs + 'ms linear';
        requestAnimationFrame(function () { fill.style.width = '100%'; });
      }

      function render() {
        slides.forEach(function (slide, i) {
          var active = i === index;
          slide.classList.toggle('is-active', active);
          // slides stack absolutely on top of each other, so an
          // IntersectionObserver can't tell active from inactive — only
          // the active slide's video should ever be decoding/playing.
          // Deliberately a different attribute than the generic
          // [data-rv-lazy-video] observer below, so that observer doesn't
          // also try to play every stacked (including inactive) slide.
          var video = slide.querySelector('[data-rv-hero-video]');
          if (!video) return;
          if (active) {
            var source = video.querySelector('source[data-src]');
            if (source) {
              source.src = source.dataset.src;
              source.removeAttribute('data-src');
              video.load();
            }
            video.play().catch(function () {});
          } else {
            video.pause();
          }
        });
        dots.forEach(function (dot, i) {
          var active = i === index;
          dot.classList.toggle('is-active', active);
          setDotFill(dot, active);
        });
      }

      function goTo(next) {
        index = ((next % slides.length) + slides.length) % slides.length;
        render();
      }

      function restartAutoAdvance() {
        if (timer) clearInterval(timer);
        if (prefersReducedMotion) return;
        timer = setInterval(function () { goTo(index + 1); }, autoAdvanceMs);
      }

      if (prevBtn) prevBtn.addEventListener('click', function () { goTo(index - 1); restartAutoAdvance(); });
      if (nextBtn) nextBtn.addEventListener('click', function () { goTo(index + 1); restartAutoAdvance(); });
      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          goTo(parseInt(dot.getAttribute('data-index'), 10) || 0);
          restartAutoAdvance();
        });
      });

      var pointerStartX = null;
      root.addEventListener('pointerdown', function (e) { pointerStartX = e.clientX; });
      root.addEventListener('pointerup', function (e) {
        if (pointerStartX === null) return;
        var delta = e.clientX - pointerStartX;
        pointerStartX = null;
        if (Math.abs(delta) > 50) {
          goTo(delta < 0 ? index + 1 : index - 1);
          restartAutoAdvance();
        }
      });

      root.addEventListener('mouseenter', function () { if (timer) clearInterval(timer); });
      root.addEventListener('mouseleave', restartAutoAdvance);

      render();
      restartAutoAdvance();
    });
  }

  /* --------------------------------------------------------------------
   * Testimonials — single-slide spotlight carousel. Slides cross-fade
   * (stacked absolutely, no transform strip), each active slide's words
   * stagger in via per-word transition-delay set inline by Liquid, the
   * oversized index number and vertical rail progress line update in JS,
   * and the rail fill runs as a real 6s countdown synced to auto-advance.
   * ------------------------------------------------------------------ */
  function initTestimonialCarousel() {
    var carousels = document.querySelectorAll('[data-rv-carousel]');
    if (!carousels.length) return;

    carousels.forEach(function (root) {
      var slides = Array.prototype.slice.call(root.querySelectorAll('[data-rv-carousel-slide]'));
      var dots = Array.prototype.slice.call(root.querySelectorAll('[data-rv-carousel-dot]'));
      var prevBtn = root.querySelector('[data-rv-carousel-prev]');
      var nextBtn = root.querySelector('[data-rv-carousel-next]');
      var bignum = root.querySelector('[data-rv-carousel-bignum]');
      var railFill = root.querySelector('[data-rv-carousel-progress-fill]');
      var stage = root.querySelector('.rv-tc__stage');
      if (slides.length < 2) return;

      var index = 0;
      var autoAdvanceMs = 6000;
      var timer = null;

      function resetRailFill() {
        if (!railFill) return;
        railFill.style.transition = 'none';
        railFill.style.height = '0%';
        railFill.style.width = '0%';
        // force reflow so the next transition actually restarts
        void railFill.offsetWidth;
        if (prefersReducedMotion) return;
        railFill.style.transition = 'height ' + autoAdvanceMs + 'ms linear, width ' + autoAdvanceMs + 'ms linear';
        requestAnimationFrame(function () {
          railFill.style.height = '100%';
          railFill.style.width = '100%';
        });
      }

      function render() {
        slides.forEach(function (slide, i) { slide.classList.toggle('is-active', i === index); });
        dots.forEach(function (dot, i) { dot.classList.toggle('is-active', i === index); });
        if (bignum) bignum.textContent = String(index + 1).length < 2 ? '0' + (index + 1) : String(index + 1);
        resetRailFill();
      }

      function goTo(next) {
        index = ((next % slides.length) + slides.length) % slides.length;
        render();
      }

      function restartAutoAdvance() {
        if (timer) clearInterval(timer);
        resetRailFill();
        if (prefersReducedMotion) return;
        timer = setInterval(function () { goTo(index + 1); }, autoAdvanceMs);
      }

      if (prevBtn) prevBtn.addEventListener('click', function () { goTo(index - 1); restartAutoAdvance(); });
      if (nextBtn) nextBtn.addEventListener('click', function () { goTo(index + 1); restartAutoAdvance(); });
      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          goTo(parseInt(dot.getAttribute('data-index'), 10) || 0);
          restartAutoAdvance();
        });
      });

      // swipe on the stage — no transform strip to drag, just a distance check
      var pointerStartX = null;
      if (stage) {
        stage.addEventListener('pointerdown', function (e) { pointerStartX = e.clientX; });
        stage.addEventListener('pointerup', function (e) {
          if (pointerStartX === null) return;
          var delta = e.clientX - pointerStartX;
          pointerStartX = null;
          if (Math.abs(delta) > 40) {
            goTo(delta < 0 ? index + 1 : index - 1);
            restartAutoAdvance();
          }
        });
      }

      root.addEventListener('mouseenter', function () { if (timer) clearInterval(timer); });
      root.addEventListener('mouseleave', restartAutoAdvance);

      render();
      restartAutoAdvance();
    });
  }

  /* --------------------------------------------------------------------
   * Lazy background/showcase video — background-style <video> elements
   * (category hero cards, showcase, testimonials, newsletter, hero
   * slider) used to carry a plain `autoplay` attribute, meaning every one
   * of them started decoding and playing the instant its metadata loaded,
   * whether or not it was anywhere near the viewport. On a page with 8-9
   * such videos that's a real amount of concurrent CPU/GPU/bandwidth work
   * competing at once, which is very likely why the site felt heavier
   * than it needed to. This defers both the network request (via
   * data-src on the <source>) and playback until the video is about to
   * enter view, and pauses it again once it scrolls well out of view.
   * ------------------------------------------------------------------ */
  /* --------------------------------------------------------------------
   * Custom cursor — desktop / fine-pointer devices only. Bails out
   * completely on touch/coarse pointers (tablet, mobile) and under
   * prefers-reduced-motion, so the native cursor is simply never touched
   * on those devices. A dot tracks the pointer 1:1; a ring trails behind
   * it with eased (lerp) motion for the elegant "unhurried" feel, and
   * unfolds from a diamond (brand's default motif for anything circular)
   * into an upright square whenever it crosses something interactive —
   * the unfold itself is the "you can click this" signal. Image/gallery
   * cards get a wider "View" state; horizontal sliders get a "Drag" state.
   * ------------------------------------------------------------------ */
  function initCustomCursor() {
    if (prefersReducedMotion) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    // three squares rotated 15deg apart bloom into a 12-point star with an
    // inscribed circle for the plain-hover state — a bespoke motif rather
    // than a generic circle, in keeping with the brand's square/diamond motif
    var motifSvg = '<svg viewBox="0 0 100 100">' +
      '<rect x="12" y="12" width="76" height="76"/>' +
      '<rect x="12" y="12" width="76" height="76" transform="rotate(15 50 50)"/>' +
      '<rect x="12" y="12" width="76" height="76" transform="rotate(30 50 50)"/>' +
      '<circle cx="50" cy="50" r="28"/>' +
      '</svg>';

    // elegant floral arrows: a slender stem topped/tailed by a pointed
    // leaf-petal (two quadratic curves meeting at sharp points) instead of
    // a plain chevron — still reads clearly as an up/down arrow at 11px,
    // but ties the "scroll" cursor state back to the brand's rose motif.
    var scrollArrowsSvg = '<span class="rv-cursor-scroll-arrows">' +
      '<svg class="rv-cursor-scroll-arrow rv-cursor-scroll-arrow--up" viewBox="0 0 16 22"><path d="M8 21V9" stroke="currentColor" stroke-width="1.3" fill="none" stroke-linecap="round"/><path d="M8 2 Q11.5 6 8 10.5 Q4.5 6 8 2 Z" fill="currentColor"/></svg>' +
      '<svg class="rv-cursor-scroll-arrow rv-cursor-scroll-arrow--down" viewBox="0 0 16 22"><path d="M8 1V13" stroke="currentColor" stroke-width="1.3" fill="none" stroke-linecap="round"/><path d="M8 20 Q11.5 16 8 11.5 Q4.5 16 8 20 Z" fill="currentColor"/></svg>' +
      '</span>';

    var ring = document.createElement('div');
    ring.className = 'rv-cursor-ring';
    ring.innerHTML = '<span class="rv-cursor-ring__shape">' +
      '<span class="rv-cursor-motif"><span class="rv-cursor-motif__spin">' + motifSvg + '</span></span>' +
      scrollArrowsSvg +
      '<span class="rv-cursor-ring__label"></span>' +
      '</span>';
    var dot = document.createElement('div');
    dot.className = 'rv-cursor-dot';
    document.body.appendChild(ring);
    document.body.appendChild(dot);

    var label = ring.querySelector('.rv-cursor-ring__label');

    var mouseX = -100, mouseY = -100;
    var ringX = -100, ringY = -100;
    var idleTimer = null;

    function wake() {
      document.body.classList.remove('rv-cursor-idle');
      clearTimeout(idleTimer);
      idleTimer = setTimeout(function () {
        document.body.classList.add('rv-cursor-idle');
      }, 2600);
    }

    window.addEventListener('mousemove', function (e) {
      document.body.classList.add('rv-cursor-active');
      mouseX = e.clientX;
      mouseY = e.clientY;
      dot.style.transform = 'translate(' + mouseX + 'px,' + mouseY + 'px)';
      wake();
    }, { passive: true });

    document.addEventListener('mouseleave', function () {
      document.body.classList.add('rv-cursor-idle');
    });

    function frame() {
      ringX += (mouseX - ringX) * 0.18;
      ringY += (mouseY - ringY) * 0.18;
      ring.style.transform = 'translate(' + ringX + 'px,' + ringY + 'px)';
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);

    var viewSelector = '.rv-mosaic__card, .rv-product-card__media-link, .rv-showcase__item, .rv-cat__hero, .rv-cat__shop, .rv-about__team-item, .rv-about__cat-card';
    var dragSelector = '[data-rv-slider-track], [data-rv-carousel-track], .rv-mosaic__track';
    var scrollSelector = '.rv-gallery3d__canvas';
    var hoverSelector = 'a, button, .rv-btn, input, select, textarea, [role="button"]';

    // surfaces that render cherry or maroon: the cherry-filled accent
    // button/sale badge stay that color in BOTH themes, while the
    // section-level ones only turn cherry/maroon once light mode is
    // active (dark mode leaves them plain noir, where the cherry cursor
    // already reads fine) — see "is-on-red" in theme.css for the actual
    // cotton-swap this drives.
    var redSurfaceAlways = '.rv-btn--accent, .rv-badge--sale';
    var redSurfaceLightOnly = '.rv-featured--cherry-light, .rv-marquee, .rv-mosaic, .rv-usp, .rv-announce';

    document.addEventListener('mouseover', function (e) {
      var t = e.target;
      if (!(t instanceof Element)) return;
      ring.classList.remove('is-hover', 'is-view', 'is-drag', 'is-scroll');
      if (t.closest(scrollSelector)) {
        ring.classList.add('is-scroll');
        label.textContent = 'Scroll';
      } else if (t.closest(viewSelector)) {
        ring.classList.add('is-view');
        label.textContent = 'View';
      } else if (t.closest(dragSelector)) {
        ring.classList.add('is-drag');
        label.textContent = 'Drag';
      } else if (t.closest(hoverSelector)) {
        ring.classList.add('is-hover');
      }

      var isLight = document.documentElement.getAttribute('data-theme') === 'light';
      var onRed = !!t.closest(redSurfaceAlways) || (isLight && !!t.closest(redSurfaceLightOnly));
      ring.classList.toggle('is-on-red', onRed);
      dot.classList.toggle('is-on-red', onRed);
    }, { passive: true });
  }

  function initLazyVideo() {
    var videos = document.querySelectorAll('[data-rv-lazy-video]');
    if (!videos.length) return;

    if (!('IntersectionObserver' in window)) {
      videos.forEach(function (video) {
        var source = video.querySelector('source[data-src]');
        if (source) source.src = source.dataset.src;
        video.load();
        video.play().catch(function () {});
      });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        if (entry.isIntersecting) {
          var source = video.querySelector('source[data-src]');
          if (source) {
            source.src = source.dataset.src;
            source.removeAttribute('data-src');
            video.load();
          }
          video.play().catch(function () {});
        } else {
          video.pause();
        }
      });
    }, { rootMargin: '300px 0px' });

    videos.forEach(function (video) { io.observe(video); });
  }

  /* --------------------------------------------------------------------
   * Testimonials ticker — continuous name/company strip below the
   * testimonials carousel. The set of items is cloned enough times at
   * runtime to guarantee the track is at least 2x the viewport width
   * before animating, so the loop always tiles seamlessly (a fixed 2x
   * duplication in markup would leave a visible blank gap whenever there
   * are few testimonials / short names and the content is narrower than
   * the viewport — exactly the bug this fixes). Position is driven by JS
   * transform rather than a CSS keyframe percentage, since the clone
   * count — and therefore what "50%" would even mean — varies by content
   * and viewport.
   * ------------------------------------------------------------------ */
  function initTicker() {
    var tracks = document.querySelectorAll('[data-rv-ticker-track]');
    if (!tracks.length) return;

    tracks.forEach(function (track) {
      var original = track.querySelector('[data-rv-ticker-set]');
      var container = track.parentElement;
      if (!original || !container) return;

      var setWidth = 0;

      function fill() {
        Array.prototype.slice.call(track.querySelectorAll('[data-rv-ticker-clone]')).forEach(function (el) { el.remove(); });
        track.style.transform = 'translateX(0)';
        setWidth = original.getBoundingClientRect().width;
        if (setWidth <= 0) return;
        var containerWidth = container.getBoundingClientRect().width;
        var needed = Math.ceil((containerWidth * 2) / setWidth) + 1;
        for (var i = 0; i < needed; i++) {
          var clone = original.cloneNode(true);
          clone.setAttribute('data-rv-ticker-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          track.appendChild(clone);
        }
      }

      fill();
      window.addEventListener('resize', throttle(fill, 300));

      if (prefersReducedMotion) return;

      var x = 0;
      var speed = 0.45;
      var paused = false;
      container.addEventListener('mouseenter', function () { paused = true; });
      container.addEventListener('mouseleave', function () { paused = false; });

      // same off-screen gating as the scroll-velocity marquee above — no
      // reason to keep animating a ticker the user can't currently see
      var running = false;

      function frame() {
        if (!running) return;
        if (!paused && setWidth > 0) {
          x -= speed;
          if (x <= -setWidth) x += setWidth;
          track.style.transform = 'translateX(' + x.toFixed(1) + 'px)';
        }
        requestAnimationFrame(frame);
      }
      function start() {
        if (running) return;
        running = true;
        requestAnimationFrame(frame);
      }
      function stop() { running = false; }

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) { entry.isIntersecting ? start() : stop(); });
        }, { rootMargin: '100px 0px' }).observe(container);
      } else {
        start();
      }
    });
  }

  /* --------------------------------------------------------------------
   * Editorial slider (gallery-mosaic) — continuous auto-scroll marquee of
   * portrait cards using native horizontal scroll (so touch/trackpad swipe
   * and keyboard scrolling keep working for free). Content is duplicated
   * once in the markup; when auto-scroll passes the halfway point it snaps
   * back by that same half-width, which is imperceptible since the second
   * half is an identical copy — giving a seamless infinite loop without a
   * transform-based rig. Pauses on hover and while the user is dragging.
   * ------------------------------------------------------------------ */
  /* --------------------------------------------------------------------
   * Numbered-showcase mobile accordion — a vanilla-JS/CSS translation of
   * the "expanding cards" interaction: tap a collapsed card to expand it
   * (flex-grow transition, driven purely by CSS via the .is-active
   * class); tap the card that's ALREADY expanded and it just follows its
   * link like a normal <a>, no special-casing needed for that. Desktop
   * ignores .is-active entirely (different stylesheet rules), so this is
   * safe to run unconditionally rather than gating it to mobile widths.
   * ------------------------------------------------------------------ */
  function initAccordionCards() {
    var roots = document.querySelectorAll('[data-rv-accordion]');
    if (!roots.length) return;

    roots.forEach(function (root) {
      var items = Array.prototype.slice.call(root.querySelectorAll('[data-rv-accordion-item]'));
      if (items.length < 2) return;

      items.forEach(function (item) {
        item.addEventListener('click', function (e) {
          if (!item.classList.contains('is-active')) {
            e.preventDefault();
            items.forEach(function (it) {
              var active = it === item;
              it.classList.toggle('is-active', active);
              if (it.hasAttribute('aria-expanded')) it.setAttribute('aria-expanded', String(active));
              // optional aria-controls link: animate the linked panel's
              // max-height to its measured content height rather than a
              // guessed constant, so open/close timing always matches the
              // actual text length (grid-template-rows 0fr/1fr looks
              // equivalent on paper but some engines never resolve the
              // transitioned value when it's set via a class toggle rather
              // than inline style, leaving the panel stuck shut)
              var panelId = it.getAttribute('aria-controls');
              if (panelId) {
                var panel = document.getElementById(panelId);
                if (panel) panel.style.maxHeight = active ? panel.scrollHeight + 'px' : '0px';
              }
            });
          }
        });
      });
    });
  }

  /* --------------------------------------------------------------------
   * Magnetic buttons — elements marked data-rv-magnetic nudge toward the
   * cursor within a small radius, snapping back on mouseleave. Desktop
   * fine-pointer only; a generic, reusable micro-interaction rather than
   * a one-off tied to any single section.
   * ------------------------------------------------------------------ */
  function initMagneticButtons() {
    if (prefersReducedMotion) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    var els = document.querySelectorAll('[data-rv-magnetic]');
    if (!els.length) return;

    els.forEach(function (el) {
      var strength = parseFloat(el.getAttribute('data-rv-magnetic')) || 0.3;
      el.addEventListener('mousemove', function (e) {
        var rect = el.getBoundingClientRect();
        var x = e.clientX - (rect.left + rect.width / 2);
        var y = e.clientY - (rect.top + rect.height / 2);
        el.style.transform = 'translate(' + (x * strength).toFixed(1) + 'px,' + (y * strength).toFixed(1) + 'px)';
      });
      el.addEventListener('mouseleave', function () {
        el.style.transform = 'translate(0,0)';
      });
    });
  }

  function initEditorialSlider() {
    var sliders = document.querySelectorAll('[data-rv-slider]');
    if (!sliders.length) return;

    sliders.forEach(function (root) {
      var track = root.querySelector('[data-rv-slider-track]');
      var prevBtn = root.querySelector('[data-rv-slider-prev]');
      var nextBtn = root.querySelector('[data-rv-slider-next]');
      if (!track) return;

      var half = track.scrollWidth / 2;
      window.addEventListener('resize', throttle(function () { half = track.scrollWidth / 2; }, 200));

      var paused = false;
      var speed = 0.7;

      function nudge(dir) {
        var first = track.children[0];
        var cardWidth = first ? first.getBoundingClientRect().width + 16 : 260;
        track.scrollBy({ left: cardWidth * dir, behavior: 'smooth' });
      }
      if (prevBtn) prevBtn.addEventListener('click', function () { nudge(-1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { nudge(1); });

      root.addEventListener('mouseenter', function () { paused = true; });
      root.addEventListener('mouseleave', function () { paused = false; });
      track.addEventListener('pointerdown', function () { paused = true; });
      track.addEventListener('pointerup', function () {
        setTimeout(function () { paused = false; }, 700);
      });
      track.addEventListener('pointercancel', function () { paused = false; });

      if (prefersReducedMotion) return;

      var running = false;
      function frame() {
        if (!running) return;
        if (!paused && half > 0) {
          track.scrollLeft += speed;
          if (track.scrollLeft >= half) track.scrollLeft -= half;
        }
        requestAnimationFrame(frame);
      }
      function start() {
        if (running) return;
        running = true;
        requestAnimationFrame(frame);
      }
      function stop() { running = false; }

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) { entry.isIntersecting ? start() : stop(); });
        }, { rootMargin: '100px 0px' }).observe(root);
      } else {
        start();
      }
    });
  }

  /* --------------------------------------------------------------------
   * Header dropdown nav — hover/focus handle desktop pointer + keyboard
   * via CSS; this adds tap-to-toggle for touch devices (no hover) and
   * closes on outside click / Escape.
   * ------------------------------------------------------------------ */
  function initNavDropdowns() {
    var items = document.querySelectorAll('.rv-header__nav-item--has-dropdown');
    if (!items.length) return;

    function closeAll(except) {
      items.forEach(function (item) {
        if (item === except) return;
        item.classList.remove('is-open');
        var trigger = item.querySelector('.rv-header__nav-trigger');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    }

    // dropdowns are centered under their trigger by default, which can
    // push them past the viewport edge for items near the left/right ends
    // of the nav — measure and nudge back on-screen before it's shown.
    function positionDropdown(item) {
      var dropdown = item.querySelector('.rv-header__dropdown');
      if (!dropdown) return;
      dropdown.style.setProperty('--rv-shift', '0px');
      var rect = dropdown.getBoundingClientRect();
      var margin = 16;
      var shift = 0;
      if (rect.left < margin) shift = margin - rect.left;
      else if (rect.right > window.innerWidth - margin) shift = (window.innerWidth - margin) - rect.right;
      if (shift !== 0) dropdown.style.setProperty('--rv-shift', shift.toFixed(0) + 'px');
    }

    items.forEach(function (item) {
      var trigger = item.querySelector('.rv-header__nav-trigger');
      if (!trigger) return;

      item.addEventListener('mouseenter', function () { positionDropdown(item); });
      trigger.addEventListener('focus', function () { positionDropdown(item); });

      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        var isOpen = item.classList.contains('is-open');
        closeAll(item);
        positionDropdown(item);
        item.classList.toggle('is-open', !isOpen);
        trigger.setAttribute('aria-expanded', String(!isOpen));
      });
    });

    document.addEventListener('click', function (e) {
      if (!e.target.closest('.rv-header__nav-item--has-dropdown')) closeAll();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAll();
    });

    window.addEventListener('resize', throttle(function () {
      items.forEach(function (item) { item.querySelector('.rv-header__dropdown').style.setProperty('--rv-shift', '0px'); });
    }, 200));
  }

  /* --------------------------------------------------------------------
   * Collection filter dropdowns — same open/close/outside-click/Escape
   * pattern as the header nav dropdowns above, swapped in to replace
   * native <details> (which can't have its show/hide transition
   * animated) with a JS-toggled .is-open class the CSS can animate.
   * ------------------------------------------------------------------ */
  function initFilterDropdowns() {
    var dds = document.querySelectorAll('[data-rv-filter-dd]');
    if (!dds.length) return;

    function closeAll(except) {
      dds.forEach(function (dd) {
        if (dd === except) return;
        dd.classList.remove('is-open');
        var trigger = dd.querySelector('[data-rv-filter-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    }

    dds.forEach(function (dd) {
      var trigger = dd.querySelector('[data-rv-filter-trigger]');
      if (!trigger) return;

      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        var isOpen = dd.classList.contains('is-open');
        closeAll(dd);
        dd.classList.toggle('is-open', !isOpen);
        trigger.setAttribute('aria-expanded', String(!isOpen));
      });
    });

    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-rv-filter-dd]')) closeAll();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAll();
    });
  }

  /* --------------------------------------------------------------------
   * Scroll-velocity marquee — speed and (briefly) direction react to how
   * fast the page is being scrolled, easing back to its base drift when
   * scrolling stops. Vanilla-JS reimplementation of a framer-motion
   * velocity marquee (direction factor + velocity factor), since this
   * theme has no React/framer-motion runtime.
   * ------------------------------------------------------------------ */
  function initScrollVelocityMarquee() {
    var marquees = document.querySelectorAll('[data-rv-marquee]');
    if (!marquees.length) return;
    if (prefersReducedMotion) return;

    var lastScrollY = window.scrollY;
    var rawVelocity = 0;

    window.addEventListener(
      'scroll',
      function () {
        var current = window.scrollY;
        rawVelocity = current - lastScrollY;
        lastScrollY = current;
      },
      { passive: true }
    );

    marquees.forEach(function (el) {
      var track = el.querySelector('[data-rv-marquee-track]');
      if (!track) return;

      var baseVelocity = (parseFloat(el.getAttribute('data-velocity')) || 3) * (parseFloat(el.getAttribute('data-direction')) || -1);
      var x = 0;
      var lastFrameTime = null;
      // this loop otherwise runs forever from page load regardless of
      // whether the marquee is ever on screen — gate it with an
      // IntersectionObserver so it only spends frame budget while visible
      var running = false;

      function frame(now) {
        if (!running) return;
        if (lastFrameTime === null) lastFrameTime = now;
        var delta = Math.min(now - lastFrameTime, 64);
        lastFrameTime = now;

        // decay the raw scroll velocity so its influence fades smoothly
        // rather than snapping to 0 the instant scrolling stops
        rawVelocity *= 0.92;
        var velocityFactor = Math.max(-6, Math.min(6, rawVelocity * 0.15));

        // base drift always moves in its configured direction; scroll
        // speed amplifies it, and a sharp upward scroll can briefly
        // reverse it — mirrors the source component's direction/velocity
        // factor interplay without needing framer-motion.
        var moveBy = baseVelocity * (delta / 1000) * (1 + Math.abs(velocityFactor));
        if (velocityFactor < -2) moveBy *= -1;

        x += moveBy;
        var display = -(((x % 50) + 50) % 50);
        track.style.transform = 'translateX(' + display + '%)';

        requestAnimationFrame(frame);
      }

      function start() {
        if (running) return;
        running = true;
        lastFrameTime = null;
        requestAnimationFrame(frame);
      }
      function stop() { running = false; }

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) { entry.isIntersecting ? start() : stop(); });
        }, { rootMargin: '100px 0px' }).observe(el);
      } else {
        start();
      }
    });
  }

  /* --------------------------------------------------------------------
   * dark / light theme toggle — data-theme attr + localStorage, mirrors
   * the inline pre-paint script in layout/theme.liquid so there's no
   * flash of the wrong theme on load
   * ------------------------------------------------------------------ */
  /* persists across pages via localStorage; the inline pre-paint script in
     layout/theme.liquid already applies the saved theme to <html> before
     first render (no flash), so this just syncs the button/meta state to
     whatever's already there and saves future choices. */
  function initThemeToggle() {
    var root = document.documentElement;
    var btn = document.querySelector('[data-rv-theme-toggle]');

    function apply(mode, persist) {
      if (mode === 'light') root.setAttribute('data-theme', 'light');
      else root.removeAttribute('data-theme');
      if (btn) btn.setAttribute('aria-label', mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode');
      var meta = document.querySelector('meta[name="theme-color"]');
      if (meta) meta.setAttribute('content', mode === 'light' ? '#edebdd' : '#1b1717');
      if (persist) {
        try { localStorage.setItem('rv-theme', mode); } catch (e) {}
      }
    }

    apply(root.getAttribute('data-theme') === 'light' ? 'light' : 'dark', false);

    if (!btn) return;
    btn.addEventListener('click', function () {
      apply(root.getAttribute('data-theme') === 'light' ? 'dark' : 'light', true);
    });
  }

  /* --------------------------------------------------------------------
   * utils
   * ------------------------------------------------------------------ */
  function throttle(fn, wait) {
    var last = 0;
    return function () {
      var now = Date.now();
      if (now - last >= wait) {
        last = now;
        fn.apply(this, arguments);
      }
    };
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    initReveals();
    initParallax();
    initHeaderScroll();
    initThemeToggle();
    initAnnouncementBar();
    initSearchToggle();
    initDrawers();
    initScrollVelocityMarquee();
    initNavDropdowns();
    initFilterDropdowns();
    initAccordionCards();
    initHeroSlider();
    initTestimonialCarousel();
    initTicker();
    initEditorialSlider();
    initLazyVideo();
    initCustomCursor();
    initMagneticButtons();

    // exposed so theme.js can re-run it after AJAX-injecting fresh
    // [data-rv-slider] markup (e.g. product recommendations), since this
    // file's own ready() call already ran and won't fire again
    window.RV = window.RV || {};
    window.RV.initEditorialSlider = initEditorialSlider;
  });
})();
